import type { Api_Video } from '@core/api'
import { getVideoPoster } from '@core/helpers/video'
import escape from '@core/lodash/escape'

function formatISO8601Duration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  let duration = 'PT'
  if (hours > 0) {
    duration += `${hours}H`
  }
  if (minutes > 0) {
    duration += `${minutes}M`
  }
  if (secs > 0 || duration === 'PT') {
    duration += `${secs}S`
  }

  return duration
}

function getVideoDescription(video: Api_Video): string {
  const hashtags = (video.hashtags || []).map((h) => `#${h}`).join(' ')
  return `${video.description ?? video.caption} ${hashtags}`.trim()
}

const FIREWORK_PUBLISHER = {
  '@type': 'Organization',
  name: 'Firework',
  logo: {
    '@type': 'ImageObject',
    url: 'https://cdn1.fireworktv.com/medias/2021/2/15/1613429354-wvuymplk/120_120/120x120.png',
    width: 120,
    height: 120,
  },
} as const

function getBroadcastService() {
  return {
    '@type': 'BroadcastService' as const,
    name: document.title || window.location.hostname,
    url: window.location.href,
  }
}

/**
 * Schema.org BroadcastEvent for currently-live streams.
 * @see https://schema.org/BroadcastEvent
 */
export function buildBroadcastEventSchema(video: Api_Video): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BroadcastEvent',
    name: escape(video.caption),
    description: escape(getVideoDescription(video)),
    startDate: video.live_stream_started_at || video.live_stream_scheduled_at,
    ...(video.live_stream_ended_at && { endDate: video.live_stream_ended_at }),
    isLiveBroadcast: true,
    videoFormat: 'HD',
    publishedOn: getBroadcastService(),
    workPerformed: {
      '@type': 'VideoObject',
      name: escape(video.caption),
      description: escape(getVideoDescription(video)),
      thumbnailUrl: [getVideoPoster(video)],
      contentUrl: video.live_stream_playback_url,
    },
  }
}

/**
 * Schema.org Event for upcoming/scheduled sessions.
 * @see https://schema.org/Event
 */
export function buildEventSchema(video: Api_Video): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: escape(video.caption),
    description: escape(getVideoDescription(video)),
    startDate: video.live_stream_scheduled_at,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: {
      '@type': 'VirtualLocation',
      url: video.web_share_url,
    },
    organizer: {
      '@type': 'Person',
      name: escape(video.creator.name),
      url: `https://fw.tv/${escape(video.creator.username)}`,
    },
    image: [getVideoPoster(video)],
    subEvent: {
      '@type': 'BroadcastEvent',
      name: escape(video.caption),
      isLiveBroadcast: true,
      startDate: video.live_stream_scheduled_at,
      publishedOn: getBroadcastService(),
    },
  }
}

/**
 * Schema.org VideoObject for replays and regular videos.
 * @see https://schema.org/VideoObject
 */
export function buildVideoObjectSchema(video: Api_Video): object {
  const source = video.video_sources[0]
  const contentUrl = source?.src
  const duration = video.live_stream_duration || video.duration

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: escape(video.caption),
    description: escape(getVideoDescription(video)),
    thumbnailUrl: [getVideoPoster(video)],
    uploadDate: video.inserted_at,
    duration: duration ? formatISO8601Duration(duration) : undefined,
    publisher: FIREWORK_PUBLISHER,
    datePublished: video.published_at,
    contentUrl,
  }
}
